import React, { useState, useEffect } from "react";
import { X, Loader } from "lucide-react";
import { useApi } from "../hooks/useApi";

const SurfaceScanEnrollVendorModal = ({ isOpen, onClose, onEnrolled, existingVendorIds = [], existingDomains = [] }) => {
    const [vendors, setVendors] = useState([]);
    const [selectedVendorId, setSelectedVendorId] = useState("");
    const [domain, setDomain] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const apiCall = useApi();

    useEffect(() => {
        if (isOpen) {
            setSelectedVendorId("");
            setDomain("");
            setError(null);
            fetchVendors();
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchVendors = async () => {
        setLoading(true);
        try {
            const res = await apiCall("GET", `/TPRMVendor/vendors?pageSize=100`);
            const items = res?.items || res || [];
            const available = items.filter(v =>
                v.vendorDomain && !existingVendorIds.includes(v.vendorId)
            );
            setVendors(available);
        } catch (err) {
            setError("Failed to load vendors");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!selectedVendorId) return;

        const d = domain.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
        if (!d) {
            setError("A domain is required for surface scan monitoring.");
            return;
        }
        if (existingDomains.some(x => x.toLowerCase() === d)) {
            setError("This domain is already enrolled for surface scan monitoring.");
            return;
        }

        setSaving(true);
        setError(null);
        try {
            onEnrolled?.(parseInt(selectedVendorId, 10), d);
            onClose();
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Failed to enroll vendor");
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-6 border border-border w-full max-w-lg shadow-lg rounded-md bg-theme-secondary">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-content-primary">Add Vendor for Surface Scan Monitoring</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                )}

                <form onSubmit={handleSave} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-content-secondary mb-2">Vendor</label>
                        <select
                            value={selectedVendorId}
                            onChange={(e) => {
                                const id = e.target.value;
                                setSelectedVendorId(id);
                                const vendor = vendors.find(v => v.vendorId.toString() === id);
                                setDomain(vendor?.vendorDomain || "");
                            }}
                            className="w-full border border-border rounded-md px-3 py-2 bg-theme-primary text-content-primary"
                            disabled={loading}
                        >
                            <option value="">-- Select a Vendor --</option>
                            {vendors.map((v) => (
                                <option key={v.vendorId} value={v.vendorId} title={v.vendorName}>
                                    {v.vendorName}
                                </option>
                            ))}
                        </select>
                        {!loading && vendors.length === 0 && (
                            <p className="text-sm text-content-secondary mt-2">
                                No vendors with domains are available for enrollment. Add vendors with domains in the TPRM module first.
                            </p>
                        )}
                    </div>

                    {selectedVendorId && (
                        <div>
                            <label className="block text-sm font-medium text-content-secondary mb-1">Domain</label>
                            <input
                                type="text"
                                value={domain}
                                onChange={(e) => setDomain(e.target.value)}
                                placeholder="e.g., example.com"
                                className="w-full border border-border rounded-md px-3 py-2 bg-theme-primary text-content-primary"
                            />
                            <p className="mt-1 text-sm text-content-secondary">
                                Verify or update the domain to monitor for this vendor.
                            </p>
                        </div>
                    )}

                    <div className="flex justify-end space-x-3 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-border text-content-secondary rounded-md hover:bg-theme-primary"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={!selectedVendorId || saving}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-700 text-white rounded-md hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {saving ? (
                                <>
                                    <Loader className="h-4 w-4 animate-spin" />
                                    Adding...
                                </>
                            ) : (
                                "Add Vendor"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SurfaceScanEnrollVendorModal;
